import { useState } from 'react'
import { uploadedMarkup } from './uploadedMarkup.js'

/**
 * Bringing a design in from somewhere else.
 *
 * Most people's first template already exists — a designer sent it, another
 * tool made it, the last agency left it behind — and rebuilding it block by
 * block to get started is the reason a builder goes unused. Paste it, or pick
 * the file.
 *
 * **It opens in Compose rather than saving straight away.** Nobody should
 * discover what they uploaded by finding it in the gallery: the allowlist may
 * have dropped something, and a name and a subject are still needed. So this
 * hands the markup to Compose and the existing save path does the rest — there
 * is no second way to write a template, which is the whole reason the editor
 * that used to live on this screen was deleted.
 *
 * The file is read here and never sent anywhere. There is no upload endpoint
 * and no stored file: what crosses the wire is the design, when it is saved,
 * exactly as one built by hand would.
 */

export function TemplateUpload({
  onOpen,
  onClose,
}: {
  /** Hands the markup to Compose. */
  readonly onOpen: (html: string) => void
  readonly onClose: () => void
}) {
  const [written, setWritten] = useState('')
  const [problem, setProblem] = useState<string | undefined>(undefined)

  function take(fileName: string, text: string) {
    const found = uploadedMarkup(fileName, text)
    if (found.status === 'markup') {
      onOpen(found.html)
      return
    }
    setProblem(REFUSALS[found.status])
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-6">
      <div className="flex max-h-full w-full max-w-2xl flex-col rounded-card border border-line bg-surface p-5">
        <div className="mb-1 flex items-center justify-between gap-3">
          <h2 className="font-bold text-[15px] text-t1">Upload a template</h2>
          <button
            type="button"
            onClick={onClose}
            className="font-medium text-[13px] text-t3 hover:text-t1"
          >
            Close
          </button>
        </div>
        <p className="mb-3 text-[13px] text-t2">
          Paste the markup, or choose an .html file — or a .json file holding a design this product
          saved. It opens in Compose, where you name it and save it.
        </p>

        {problem === undefined ? null : (
          <p className="mb-3 rounded-field border border-danger bg-danger-soft px-3 py-2 text-[13px] text-danger">
            {problem}
          </p>
        )}

        <label
          className="mb-3 flex flex-wrap items-center gap-2 text-[13px] text-t2"
          htmlFor="file"
        >
          Choose a file
          <input
            id="file"
            type="file"
            accept=".html,.htm,.json,text/html,application/json"
            onChange={(event) => {
              const chosen = event.target.files?.[0]
              if (chosen === undefined) return
              setProblem(undefined)
              chosen
                .text()
                .then((text) => take(chosen.name, text))
                .catch(() => setProblem('We could not read that file.'))
            }}
            className="text-[12px] text-t2 file:mr-2 file:rounded-pill file:border file:border-line file:bg-surface file:px-3 file:py-1.5 file:font-medium file:text-[12px] file:text-t2"
          />
        </label>

        <textarea
          aria-label="Paste the markup"
          value={written}
          spellCheck={false}
          placeholder="…or paste it here"
          onChange={(event) => {
            setWritten(event.target.value)
            setProblem(undefined)
          }}
          className="min-h-0 flex-1 resize-none rounded-field border border-line bg-surface-2 p-3 font-mono text-[12px] text-t1 leading-relaxed"
          rows={12}
        />

        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            disabled={written.trim() === ''}
            onClick={() => take('', written)}
            className="rounded-pill bg-brand px-4 py-2 font-bold text-[13px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
          >
            Open in Compose
          </button>
          <p className="text-[12px] text-t3">
            Anything a mail client cannot render is dropped before it is sent.
          </p>
        </div>
      </div>
    </div>
  )
}

const REFUSALS = {
  empty: 'There was nothing in that.',
  // Named rather than half-read: a json file that is not a design is far more
  // likely to be the wrong file than a design we failed to understand.
  'not-a-design': 'That json does not hold a design. A design has its markup under "html".',
} as const
